import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { seedState } from '../data/seedState';
import { Family, Member, TaskTemplate } from '../types/family';
import { OccurrenceState } from '../types/activity';
import { Db } from '../types/shared';
import { StoreCore } from '../types/store';
import { DEFAULT_OCC } from '../utils/db';
import { computeWeek, WeekSummary } from '../utils/progress';
import { BonusLine, derivedBonusToday, ledgerSum } from '../utils/bonus';
import { isGivenAway, skipKey, sportEventsFor } from '../utils/today';
import { DEMO_DATE, WEEK_ID, WEEK_START } from '../data/demo';
import { dutyChecksFor, isDuty } from '../utils/duties';
import { DutyCheck, DutyReport, DutySlot } from '../types/shared';
import { WizardInput } from '../utils/familyFactory';
import { useTaskActions } from '../hooks/useTaskActions';
import { useTimerActions } from '../hooks/useTimerActions';
import { useRewardActions } from '../hooks/useRewardActions';
import { useExceptionActions } from '../hooks/useExceptionActions';
import { useSharedActions } from '../hooks/useSharedActions';
import { useFamilyEditing } from '../hooks/useFamilyEditing';
const STORAGE_KEY = 'do-it-dont-poo-it:prototip';
const STORAGE_VERSION = 5;
const VIEWER_KEY = 'do-it-dont-poo-it:kdo';
function loadPicked(): Record<string, string> {
  try {
    return JSON.parse(window.localStorage.getItem(VIEWER_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}
function loadState(): Db {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        version: number;
        state: Db;
      };
      if (parsed.version === STORAGE_VERSION && parsed.state) return parsed.state;
    }
  } catch {
    // Neveljavno shranjeno stanje – uporabi vzorec.
  }
  return JSON.parse(JSON.stringify(seedState)) as Db;
}
interface BaseValue {
  db: Db;
  family: Family;
  viewer: Member;
  isAdmin: boolean;
  offline: boolean;
  familyOcc: Record<string, OccurrenceState>;
  activeMembers: Member[];
  myTemplates: TaskTemplate[];
  getOcc: (templateId: string) => OccurrenceState;
  templateById: (id?: string) => TaskTemplate | undefined;
  memberById: (id?: string) => Member | undefined;
  reviewQueue: TaskTemplate[];
  approvalsCount: number;
  weekFor: (memberId: string) => WeekSummary;
  bonusTodayFor: (memberId: string) => BonusLine[];
  balanceOf: (memberId: string) => number;
  schoolPEFor: (memberId: string) => boolean;
  pendingSyncCount: number;
  myFamilies: Family[];
  switchFamily: (familyId: string) => void;
  createFamilyAndSwitch: (input: WizardInput) => void;
  resetPrototype: () => void;
  /** Prototip brez prijave: dokler nihče ni izbran, se pokaže zaslon »Kdo si?«. */
  needsPick: boolean;
  pickViewer: (memberId: string) => void;
  changePerson: () => void;
  isEventSkipped: (eventId: string, memberId: string) => boolean;
  toggleSkipEvent: (eventId: string) => void;
  dutyChecksOf: (templateId: string) => DutyCheck[];
  dutyReportsOf: (templateId: string) => DutyReport[];
  setDutyCheck: (templateId: string, slot: DutySlot, result: DutyCheck['result'] | null, note?: string) => void;
  setDutyFixed: (templateId: string, slot: DutySlot, fixed: boolean) => void;
  reportDuty: (templateId: string, text: string) => void;
}
type AppStateValue = BaseValue & ReturnType<typeof useTaskActions> & ReturnType<typeof useTimerActions> & Omit<ReturnType<typeof useRewardActions>, never> & ReturnType<typeof useExceptionActions> & ReturnType<typeof useSharedActions> & Omit<ReturnType<typeof useFamilyEditing>, 'createFamily'>;
const AppStateContext = createContext<AppStateValue | null>(null);
interface AppStateProviderProps {
  familyId: string;
  offline: boolean;
  children: ReactNode;
}
export function AppStateProvider({
  familyId,
  offline,
  children
}: AppStateProviderProps) {
  const [db, setDb] = useState<Db>(loadState);
  const [override, setOverride] = useState<{
    familyId: string;
    viewerId: string;
  } | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>(loadPicked);
  useEffect(() => setOverride(null), [familyId]);
  useEffect(() => {
    try {
      window.localStorage.setItem(VIEWER_KEY, JSON.stringify(picked));
    } catch {
      // Izbira osebe ostane samo v pomnilniku.
    }
  }, [picked]);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: STORAGE_VERSION,
        state: db
      }));
    } catch {
      // Shranjevanje ni na voljo – stanje ostane v pomnilniku.
    }
  }, [db]);
  const homeFamily = db.families.find(f => f.id === familyId) ?? db.families[0];
  const pickedMember = homeFamily.members.find(m => m.id === picked[homeFamily.id] && !m.archived);
  const needsPick = !override && !pickedMember;
  const homeViewer = pickedMember ?? homeFamily.members.find(m => !m.archived) ?? homeFamily.members[0];
  const homeRef = `${homeFamily.id}:${homeViewer.id}`;
  const family = override && db.families.find(f => f.id === override.familyId) || homeFamily;
  const viewer = override && family.members.find(m => m.id === override.viewerId) || (family.id === homeFamily.id ? homeViewer : family.members[0]);
  const isAdmin = viewer.role === 'skrbnik';
  const familyOcc = useMemo(() => db.occ[family.id] ?? {}, [db.occ, family.id]);
  const core: StoreCore = {
    db,
    setDb,
    family,
    viewer,
    isAdmin,
    familyOcc
  };
  const task = useTaskActions(core);
  const timer = useTimerActions(core);
  const exc = useExceptionActions(core);
  const shared = useSharedActions(core);
  const {
    createFamily,
    ...editing
  } = useFamilyEditing(core);
  const getOcc = useCallback((id: string) => familyOcc[id] ?? DEFAULT_OCC, [familyOcc]);
  const templateById = useCallback((id?: string) => id ? family.templates.find(t => t.id === id) : undefined, [family]);
  const memberById = useCallback((id?: string) => family.members.find(m => m.id === id), [family]);
  const activeMembers = useMemo(() => family.members.filter(m => !m.archived), [family]);
  const myTemplates = useMemo(() => family.templates.filter(t => family.modules[t.module] && (t.assigneeIds.includes(viewer.id) && !isGivenAway(familyOcc[t.id], viewer.id) || familyOcc[t.id]?.takenBy === viewer.id)), [family, viewer.id, familyOcc]);
  const schoolPEFor = useCallback((memberId: string) => Boolean(db.schoolPE[`${family.id}:${memberId}`]), [db.schoolPE, family.id]);
  const weekFor = useCallback((memberId: string) => computeWeek(family, memberId, familyOcc, schoolPEFor(memberId), db.exceptions, sportEventsFor(family, memberId, db.skippedEvents ?? [], WEEK_START, DEMO_DATE).map(e => e.date), db.dutyChecks ?? []), [family, familyOcc, schoolPEFor, db.exceptions, db.skippedEvents, db.dutyChecks]);
  const dutyChecksOf = useCallback((templateId: string) => dutyChecksFor(db.dutyChecks ?? [], family.id, templateId), [db.dutyChecks, family.id]);
  const dutyReportsOf = useCallback((templateId: string) => (db.dutyReports ?? []).filter(r => r.familyId === family.id && r.weekId === WEEK_ID && r.templateId === templateId), [db.dutyReports, family.id]);

  /** Starš zabeleži pregled; `null` pregled odstrani. */
  const setDutyCheck = useCallback((templateId: string, slot: DutySlot, result: DutyCheck['result'] | null, note?: string) => setDb(prev => {
    const rest = (prev.dutyChecks ?? []).filter(c => !(c.familyId === family.id && c.weekId === WEEK_ID && c.templateId === templateId && c.slot === slot));
    if (!result) return {
      ...prev,
      dutyChecks: rest
    };
    return {
      ...prev,
      dutyChecks: [...rest, {
        familyId: family.id,
        weekId: WEEK_ID,
        templateId,
        slot,
        result,
        note: result === 'ni-urejeno' ? note : undefined,
        by: viewer.id,
        at: Date.now()
      }]
    };
  }), [family.id, viewer.id]);
  const setDutyFixed = useCallback((templateId: string, slot: DutySlot, fixed: boolean) => setDb(prev => ({
    ...prev,
    dutyChecks: (prev.dutyChecks ?? []).map(c => c.familyId === family.id && c.weekId === WEEK_ID && c.templateId === templateId && c.slot === slot ? {
      ...c,
      fixed
    } : c)
  })), [family.id]);
  const reportDuty = useCallback((templateId: string, text: string) => {
    setDb(prev => ({
      ...prev,
      dutyReports: [...(prev.dutyReports ?? []), {
        id: `dr-${Date.now()}`,
        familyId: family.id,
        weekId: WEEK_ID,
        templateId,
        memberId: viewer.id,
        date: DEMO_DATE,
        text
      }]
    }));
    toast.success('Javljeno. Hvala!');
  }, [family.id, viewer.id]);
  const isEventSkipped = useCallback((eventId: string, memberId: string) => (db.skippedEvents ?? []).includes(skipKey(family.id, eventId, memberId)), [db.skippedEvents, family.id]);
  const toggleSkipEvent = useCallback((eventId: string) => {
    const key = skipKey(family.id, eventId, viewer.id);
    setDb(prev => {
      const list = prev.skippedEvents ?? [];
      return {
        ...prev,
        skippedEvents: list.includes(key) ? list.filter(k => k !== key) : [...list, key]
      };
    });
  }, [family.id, viewer.id]);
  const pickViewer = useCallback((memberId: string) => {
    setOverride(null);
    setPicked(p => ({
      ...p,
      [homeFamily.id]: memberId
    }));
  }, [homeFamily.id]);
  const changePerson = useCallback(() => {
    setOverride(null);
    setPicked(p => {
      const next = {
        ...p
      };
      delete next[homeFamily.id];
      return next;
    });
  }, [homeFamily.id]);
  const {
    minutesToday
  } = timer;
  const bonusTodayFor = useCallback((memberId: string) => {
    const plan = family.plans.find(p => p.memberId === memberId)?.items.find(i => i.key === 'sport');
    const member = family.members.find(m => m.id === memberId);
    const req = member?.role === 'otrok' ? schoolPEFor(memberId) ? family.rules.sportPerWeekChildWithSchoolPE : family.rules.sportPerWeekChild : family.rules.sportPerWeekAdult;
    return derivedBonusToday(family, memberId, familyOcc, tid => minutesToday(tid, memberId), Boolean(plan && plan.doneBefore >= req));
  }, [family, familyOcc, minutesToday, schoolPEFor]);
  const balanceOf = useCallback((memberId: string) => (family.bonusSeed[memberId] ?? 0) + ledgerSum(db.ledger, family.id, memberId) + bonusTodayFor(memberId).reduce((s, l) => s + l.amount, 0), [family, db.ledger, bonusTodayFor]);
  const reward = useRewardActions(core, balanceOf);
  const reviewQueue = useMemo(() => family.templates.filter(t => !isDuty(t) && familyOcc[t.id]?.status === 'v_pregledu'), [family, familyOcc]);
  const approvalsCount = reviewQueue.length + db.rewardRequests.filter(r => r.familyId === family.id && r.status === 'caka').length + db.exceptions.filter(e => e.familyId === family.id && e.status === 'caka').length;
  const myFamilies = useMemo(() => [homeFamily, ...db.families.filter(f => f.creatorRef === homeRef)], [homeFamily, db.families, homeRef]);
  const switchFamily = useCallback((id: string) => {
    if (id === homeFamily.id) {
      setOverride(null);
      return;
    }
    const f = db.families.find(x => x.id === id && x.creatorRef === homeRef);
    if (f) setOverride({
      familyId: f.id,
      viewerId: f.members[0].id
    });
  }, [homeFamily.id, db.families, homeRef]);
  const createFamilyAndSwitch = useCallback((input: WizardInput) => {
    const fam = createFamily(input, homeRef);
    setOverride({
      familyId: fam.id,
      viewerId: fam.members[0].id
    });
  }, [createFamily, homeRef]);
  const resetPrototype = useCallback(() => {
    setOverride(null);
    setDb(JSON.parse(JSON.stringify(seedState)) as Db);
  }, []);
  const pendingSyncCount = useMemo(() => Object.values(familyOcc).filter(o => o.sync === 'pending').length + db.entries.filter(e => e.familyId === family.id && e.sync === 'pending').length, [familyOcc, db.entries, family.id]);

  // Vzorčna sinhronizacija: brez povezave ostanejo spremembe lokalno označene.
  useEffect(() => {
    if (offline || pendingSyncCount === 0) return;
    const id = window.setTimeout(() => {
      setDb(prev => {
        const occ: Db['occ'] = {};
        Object.entries(prev.occ).forEach(([fid, fam]) => {
          occ[fid] = {};
          Object.entries(fam).forEach(([tid, o]) => {
            occ[fid][tid] = {
              ...o,
              sync: 'synced'
            };
          });
        });
        return {
          ...prev,
          occ,
          entries: prev.entries.map(e => ({
            ...e,
            sync: 'synced'
          }))
        };
      });
    }, 1400);
    return () => window.clearTimeout(id);
  }, [offline, pendingSyncCount]);
  const value: AppStateValue = {
    db,
    family,
    viewer,
    isAdmin,
    offline,
    familyOcc,
    activeMembers,
    myTemplates,
    getOcc,
    templateById,
    memberById,
    reviewQueue,
    approvalsCount,
    weekFor,
    bonusTodayFor,
    balanceOf,
    schoolPEFor,
    pendingSyncCount,
    myFamilies,
    switchFamily,
    createFamilyAndSwitch,
    resetPrototype,
    needsPick,
    pickViewer,
    changePerson,
    isEventSkipped,
    toggleSkipEvent,
    dutyChecksOf,
    dutyReportsOf,
    setDutyCheck,
    setDutyFixed,
    reportDuty,
    ...task,
    ...timer,
    ...reward,
    ...exc,
    ...shared,
    ...editing
  };
  return <AppStateContext.Provider value={value} data-id="element-1301" data-mp="for5of1vhs2v">{children}</AppStateContext.Provider>;
}
export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState mora biti znotraj AppStateProvider');
  return ctx;
}